import { ReactNode } from 'react';
import cn from 'classnames';
import styles from './fieldset.module.scss';
import { useId } from 'react';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';
import { Spacing } from '../../Layout/Grid/interfaces';
import { VALIDITY } from '@/constants/validity';

export interface FieldsetProps {
    legend: ReactNode;
    theme?: 'themeHidden' | 'themeGrey' | 'themeWeb2';
    role?: string;
    required?: boolean;
    marginTop?: Spacing;
    marginBottom?: Spacing;
    invalid?: FieldValidityState;
    invalidMessage?: ReactNode;
    children: ReactNode;
}

interface Styles {
    marginTop?: Spacing;
    marginBottom?: Spacing;
}

export function Fieldset(props: FieldsetProps): React.JSX.Element {
    const style: Styles = {};

    const errorId = useId() + '-error';
    const isInvalid = props.invalid === VALIDITY.invalid;

    const legendClasses = cn(styles.fieldsetLegend, {
        [styles.themeHidden]: props.theme === 'themeHidden' || props.theme === undefined,
        [styles.themeGrey]: props.theme === 'themeGrey',
        [styles.themeWeb2]: props.theme === 'themeWeb2',
    });

    if (props.marginTop) {
        style.marginTop = props.marginTop;
    }

    if (props.marginBottom) {
        style.marginBottom = props.marginBottom;
    }

    return (
        <fieldset
            className={styles.fieldset}
            style={style}
            role={props.role}
            // A radiogroup is the only fieldset role that can be marked as required:
            // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required#associated_roles
            aria-required={(props.role === 'radiogroup' && props.required) || undefined}
            // A radiogroup is the only fieldset role that can be marked as invalid:
            // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-invalid#associated_roles
            aria-invalid={(props.role === 'radiogroup' && isInvalid) || undefined}
            aria-describedby={props.invalidMessage ? errorId : undefined}
        >
            {props.legend && <legend className={legendClasses}>{props.legend}</legend>}

            {props.invalid && props.invalidMessage && (
                <InvalidFeedback validState={props.invalid} errorId={errorId}>
                    {props.invalidMessage}
                </InvalidFeedback>
            )}

            {props.children}
        </fieldset>
    );
}
